import {
  Api,
  JOURNEY_STEP_TYPE_OPTIONS,
  IJourney,
  IJourneyStep,
} from "@model";
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Badge,
  Button,
  Collapse,
  Descriptions,
  Empty,
  Modal,
  Space,
  Spin,
  Steps,
  Table,
  Tabs,
  Tag,
  Timeline,
  Typography,
  message,
} from "antd";
import { ColumnsType } from "antd/lib/table";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

type UserJourneyStatus = "in_progress" | "completed" | "paused";

interface JourneyLogUser {
  _id: string;
  name?: string;
  email?: string;
  originLanguage?: string;
  targetLanguage?: string;
  languageLevel?: string;
  xp?: number;
  progressionLevel?: number;
  totalMissionsCompleted?: number;
  currentStreak?: number;
}

export interface JourneyStepProgress {
  stepId: string;
  status: string;
  startedAt?: string;
  completedAt?: string;
  attemptId?: string;
  chatID?: string;
  subStepProgress?: JourneyStepProgress[];
}

interface JourneyLog {
  _id: string;
  userID: string;
  status: UserJourneyStatus;
  currentStepIndex: number;
  startedAt: string;
  completedAt?: string;
  originLanguage?: string;
  targetLanguage?: string;
  stepProgress: JourneyStepProgress[];
  chatTranscripts?: ChatTranscript[];
  user: JourneyLogUser | null;
}

interface ChatHistoryItem {
  role: "user" | "assistant";
  content: string;
}

export interface LessonAttempt {
  _id: string;
  lessonID?: string;
  journeyStepId?: string;
  status?: string;
  score?: number;
  totalScore?: number;
  xpEarned?: number;
  feedback?: string;
  date?: string;
  chatHistory?: ChatHistoryItem[];
  stepAttempts?: Array<{
    stepId?: string;
    stepTitle?: string;
    status?: string;
    score?: number;
    feedback?: string;
    chatHistory?: ChatHistoryItem[];
  }>;
}

export interface JourneyMessage {
  _id?: string;
  chatID?: string;
  role?: "user" | "assistant" | string;
  message?: string;
  sendDate?: string;
  messageInputType?: string;
  mediaUrl?: string;
}

export interface ChatTranscript {
  stepId: string;
  chatID: string;
  capturedAt?: string;
  messages: JourneyMessage[];
}

interface ConversationSession {
  key: string;
  label: string;
  messages: JourneyMessage[];
}

interface JourneyRun {
  _id: string;
  status: UserJourneyStatus;
  startedAt: string;
  completedAt?: string;
  originLanguage?: string;
  targetLanguage?: string;
}

interface JourneyLogDetails {
  journey: Pick<IJourney, "_id" | "title" | "steps" | "metadata">;
  userJourney: JourneyLog;
  user: JourneyLogUser | null;
  runs?: JourneyRun[];
  lessonAttempts: LessonAttempt[];
  messages: JourneyMessage[];
}

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleString() : "—";

const EntityLink = ({
  to,
  children,
}: {
  to?: string | null;
  children: ReactNode;
}) => {
  if (!to) return <>{children}</>;
  return (
    <Link
      to={to}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: "#1890ff" }}
      onClick={(event) => event.stopPropagation()}
    >
      {children}
    </Link>
  );
};

const userEditPath = (userId?: string | null) =>
  userId ? `/panel/users/edit/${userId}` : null;

const chatLessonPath = (lessonId?: string | null) =>
  lessonId ? `/panel/chat-lessons/create/${lessonId}` : null;

const languageLabel = (user: JourneyLogUser | null) => {
  if (!user) return "—";
  if (user.originLanguage && user.targetLanguage) {
    return `${user.originLanguage} → ${user.targetLanguage}`;
  }
  return user.targetLanguage ?? user.originLanguage ?? "—";
};

const runLanguageLabel = (
  run: Pick<JourneyRun, "originLanguage" | "targetLanguage">,
) => {
  if (run.originLanguage && run.targetLanguage) {
    return `${run.originLanguage} → ${run.targetLanguage}`;
  }
  return run.targetLanguage ?? run.originLanguage;
};

const statusTag = (status?: string) => {
  const color =
    status === "completed"
      ? "green"
      : status === "in_progress"
        ? "blue"
        : status === "skipped"
          ? "orange"
          : "default";
  return <Tag color={color}>{status?.replace("_", " ") ?? "unknown"}</Tag>;
};

const stepMessageStatus = (step: {
  status?: string;
  chatHistory?: ChatHistoryItem[];
}) => {
  if (!step.chatHistory?.length) return "error";
  return step.status === "completed" ? "success" : "warning";
};

const attemptStepStatus = (attempt: LessonAttempt) => {
  if (!attempt.stepAttempts?.length) return "error";
  return attempt.stepAttempts.some((step) => step.status !== "completed")
    ? "warning"
    : "success";
};

const StatusCircle = ({
  status,
}: {
  status: "success" | "warning" | "error";
}) => (
  <span style={{ display: "inline-flex", transform: "scale(2)" }}>
    <Badge status={status} />
  </span>
);

const stepTitle = (step?: IJourneyStep, fallbackIndex = 0) =>
  step?.metadata?.en?.title?.trim() ||
  Object.values(step?.metadata ?? {}).find((entry) => entry?.title?.trim())
    ?.title ||
  `Step ${fallbackIndex + 1}`;

const stepTypeLabel = (type?: string) =>
  JOURNEY_STEP_TYPE_OPTIONS.find((option) => option.value === type)?.label ??
  type ??
  "Step";

const progressStepStatus = (
  status?: string,
): "wait" | "process" | "finish" | "error" => {
  if (status === "completed") return "finish";
  if (status === "in_progress") return "process";
  if (status === "skipped") return "error";
  return "wait";
};

const ChatHistory = ({ items = [] }: { items?: ChatHistoryItem[] }) => {
  if (!items.length)
    return <Typography.Text type="secondary">No messages</Typography.Text>;
  return (
    <Timeline>
      {items.map((item, index) => (
        <Timeline.Item
          color={item.role === "assistant" ? "blue" : "green"}
          key={`${item.role}-${index}`}
        >
          <Typography.Text strong>
            {item.role === "assistant" ? "AI" : "User"}
          </Typography.Text>
          <div style={{ whiteSpace: "pre-wrap" }}>{item.content}</div>
        </Timeline.Item>
      ))}
    </Timeline>
  );
};

const LessonAttemptDetails = ({ attempt }: { attempt: LessonAttempt }) => (
  <>
    <Descriptions size="small" column={3} style={{ marginBottom: 12 }}>
      <Descriptions.Item label="Attempt ID" span={3}>
        {attempt._id}
      </Descriptions.Item>
      <Descriptions.Item label="Lesson" span={3}>
        {attempt.lessonID ? (
          <EntityLink to={chatLessonPath(attempt.lessonID)}>
            {attempt.lessonID}
          </EntityLink>
        ) : (
          "—"
        )}
      </Descriptions.Item>
      <Descriptions.Item label="Score">{attempt.score ?? "—"}</Descriptions.Item>
      <Descriptions.Item label="Total score">
        {attempt.totalScore ?? "—"}
      </Descriptions.Item>
      <Descriptions.Item label="XP earned">
        {attempt.xpEarned ?? "—"}
      </Descriptions.Item>
      <Descriptions.Item label="Feedback" span={3}>
        {attempt.feedback ?? "—"}
      </Descriptions.Item>
    </Descriptions>
    {!!attempt.stepAttempts?.length && (
      <Collapse>
        {attempt.stepAttempts.map((step, index) => (
          <Collapse.Panel
            header={
              <Space size={8}>
                <StatusCircle status={stepMessageStatus(step)} />
                <span>
                  {step.stepTitle ?? `Lesson step ${index + 1}`} —{" "}
                  {step.status ?? "unknown"} — score: {step.score ?? "—"}
                </span>
              </Space>
            }
            key={`${step.stepId}-${index}`}
          >
            {step.feedback && <p>{step.feedback}</p>}
            <ChatHistory items={step.chatHistory} />
          </Collapse.Panel>
        ))}
      </Collapse>
    )}
  </>
);

const messageSpeaker = (role?: string) => {
  const normalized = String(role ?? "")
    .trim()
    .toLowerCase();
  if (normalized === "user") {
    return { label: "User", color: "green" as const };
  }
  if (normalized === "assistant") {
    return { label: "AI", color: "blue" as const };
  }
  return { label: role?.trim() || "Unknown", color: "gray" as const };
};

const CharacterMessages = ({ messages }: { messages: JourneyMessage[] }) => {
  if (!messages.length) {
    return (
      <Typography.Text type="secondary">No character messages</Typography.Text>
    );
  }
  return (
    <Timeline>
      {messages.map((item, index) => {
        const speaker = messageSpeaker(item.role);
        return (
          <Timeline.Item color={speaker.color} key={item._id ?? `${index}`}>
            <Space size={8} wrap>
              <Typography.Text strong>{speaker.label}</Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {formatDate(item.sendDate)}
              </Typography.Text>
              {item.role === "user" && item.messageInputType && (
                <Tag>{item.messageInputType}</Tag>
              )}
            </Space>
            <div style={{ whiteSpace: "pre-wrap" }}>
              {item.message ||
                (item.mediaUrl ? `Media: ${item.mediaUrl}` : "—")}
            </div>
          </Timeline.Item>
        );
      })}
    </Timeline>
  );
};

const buildStepConversationSessions = ({
  stepId,
  progress,
  transcripts = [],
  liveMessages = [],
}: {
  stepId?: string;
  progress?: JourneyStepProgress;
  transcripts?: ChatTranscript[];
  liveMessages?: JourneyMessage[];
}): ConversationSession[] => {
  if (!stepId) return [];

  const sessions: ConversationSession[] = [];
  const stepTranscripts = transcripts.filter(
    (transcript) => transcript.stepId === stepId,
  );

  stepTranscripts.forEach((transcript, index) => {
    const messages = [...(transcript.messages ?? [])].sort(
      (a, b) =>
        new Date(a.sendDate ?? 0).getTime() -
        new Date(b.sendDate ?? 0).getTime(),
    );
    if (!messages.length) return;
    // Ignore archived opener-only restarts (no learner turns).
    const hasUserTurn = messages.some(
      (item) => messageSpeaker(item.role).label === "User",
    );
    if (!hasUserTurn) return;
    sessions.push({
      key: `archive-${transcript.chatID}-${transcript.capturedAt ?? index}`,
      label: `Session ${sessions.length + 1}${
        transcript.capturedAt
          ? ` (archived ${formatDate(transcript.capturedAt)})`
          : ""
      }`,
      messages,
    });
  });

  const chatIds = collectProgressChatIds(progress);
  const currentMessages = liveMessages
    .filter((item) => item.chatID && chatIds.has(item.chatID))
    .sort(
      (a, b) =>
        new Date(a.sendDate ?? 0).getTime() -
        new Date(b.sendDate ?? 0).getTime(),
    );

  if (currentMessages.length) {
    const archivedIds = new Set(
      stepTranscripts.flatMap((transcript) =>
        (transcript.messages ?? [])
          .map((item) => item._id)
          .filter(Boolean) as string[],
      ),
    );
    const uniqueLive = currentMessages.filter(
      (item) => !item._id || !archivedIds.has(item._id),
    );
    if (uniqueLive.length) {
      sessions.push({
        key: `live-${progress?.chatID ?? stepId}`,
        label: "Current session",
        messages: uniqueLive,
      });
    }
  }

  return sessions;
};

const collectProgressChatIds = (progress?: JourneyStepProgress) => {
  if (!progress) return new Set<string>();
  const ids = new Set<string>();
  if (progress.chatID) ids.add(progress.chatID);
  for (const sub of progress.subStepProgress ?? []) {
    if (sub.chatID) ids.add(sub.chatID);
  }
  return ids;
};

const StepDetailsTree = ({
  step,
  progress,
  attempts,
  conversationSessions,
  definitionSubSteps = [],
}: {
  step: IJourneyStep;
  progress?: JourneyStepProgress;
  attempts: LessonAttempt[];
  conversationSessions: ConversationSession[];
  definitionSubSteps?: IJourneyStep[];
}) => {
  const messageCount = conversationSessions.reduce(
    (sum, session) => sum + session.messages.length,
    0,
  );
  const hasContent =
    attempts.length > 0 ||
    messageCount > 0 ||
    !!(progress?.subStepProgress?.length || definitionSubSteps.length);

  if (!hasContent) {
    return (
      <Typography.Text type="secondary">
        No lesson attempts, conversations, or sub-steps for this step
      </Typography.Text>
    );
  }

  return (
    <div style={{ marginTop: 8 }}>
      {!!attempts.length && (
        <div style={{ marginBottom: 16 }}>
          <Typography.Text strong>Lesson attempts</Typography.Text>
          <Collapse style={{ marginTop: 8 }}>
            {attempts.map((attempt) => (
              <Collapse.Panel
                header={
                  <Space size={8}>
                    <StatusCircle status={attemptStepStatus(attempt)} />
                    <span>
                      Attempt {formatDate(attempt.date)} —{" "}
                      {attempt.status ?? "unknown"}
                    </span>
                  </Space>
                }
                key={attempt._id}
              >
                <LessonAttemptDetails attempt={attempt} />
              </Collapse.Panel>
            ))}
          </Collapse>
        </div>
      )}

      {!!conversationSessions.length && (
        <div style={{ marginBottom: 16 }}>
          <Typography.Text strong>Character conversation</Typography.Text>
          <Collapse style={{ marginTop: 8 }}>
            {conversationSessions.map((session) => {
              const userCount = session.messages.filter(
                (item) => messageSpeaker(item.role).label === "User",
              ).length;
              const aiCount = session.messages.filter(
                (item) => messageSpeaker(item.role).label === "AI",
              ).length;
              return (
                <Collapse.Panel
                  header={`${session.label} — ${session.messages.length} messages (${userCount} user, ${aiCount} AI)`}
                  key={session.key}
                >
                  <CharacterMessages messages={session.messages} />
                </Collapse.Panel>
              );
            })}
          </Collapse>
        </div>
      )}

      {(definitionSubSteps.length > 0 ||
        (progress?.subStepProgress?.length ?? 0) > 0) && (
        <div>
          <Typography.Text strong>Sub-steps</Typography.Text>
          <Steps direction="vertical" size="small" style={{ marginTop: 8 }}>
            {(definitionSubSteps.length
              ? [...definitionSubSteps].sort(
                  (a, b) => (a.order ?? 0) - (b.order ?? 0),
                )
              : (progress?.subStepProgress ?? []).map((sub, index) => ({
                  stepId: sub.stepId,
                  type: step.type,
                  order: index,
                }))
            ).map((subDef, index) => {
              const subProgress = progress?.subStepProgress?.find(
                (entry) => entry.stepId === subDef.stepId,
              );
              return (
                <Steps.Step
                  key={subDef.stepId ?? `sub-${index}`}
                  status={progressStepStatus(subProgress?.status)}
                  title={
                    <Space size={8}>
                      <span>{stepTitle(subDef as IJourneyStep, index)}</span>
                      {statusTag(subProgress?.status ?? "pending")}
                    </Space>
                  }
                  description={
                    <div>
                      <div>{stepTypeLabel((subDef as IJourneyStep).type)}</div>
                      {subProgress?.startedAt && (
                        <div style={{ fontSize: 11, color: "rgba(0,0,0,0.45)" }}>
                          Started: {formatDate(subProgress.startedAt)}
                        </div>
                      )}
                      {subProgress?.completedAt && (
                        <div style={{ fontSize: 11, color: "rgba(0,0,0,0.45)" }}>
                          Done: {formatDate(subProgress.completedAt)}
                        </div>
                      )}
                    </div>
                  }
                />
              );
            })}
          </Steps>
        </div>
      )}
    </div>
  );
};

export const JourneyProgressSteps = ({
  journeySteps = [],
  stepProgress = [],
  lessonAttempts = [],
  messages = [],
  transcripts = [],
}: {
  journeySteps?: IJourneyStep[];
  stepProgress?: JourneyStepProgress[];
  lessonAttempts?: LessonAttempt[];
  messages?: JourneyMessage[];
  transcripts?: ChatTranscript[];
}) => {
  const progressById = useMemo(() => {
    const map = new Map<string, JourneyStepProgress>();
    for (const progress of stepProgress) {
      map.set(progress.stepId, progress);
    }
    return map;
  }, [stepProgress]);

  const steps = useMemo((): IJourneyStep[] => {
    const sorted = [...journeySteps].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0),
    );
    if (sorted.length) return sorted;
    return stepProgress.map((progress, index) => ({
      stepId: progress.stepId,
      type: undefined as unknown as IJourneyStep["type"],
      order: index,
      subSteps: [],
    }));
  }, [journeySteps, stepProgress]);

  if (!steps.length) {
    return <Empty description="No journey steps" />;
  }

  return (
    <div style={{ padding: "8px 4px 16px" }}>
      <Steps direction="vertical" size="small">
        {steps.map((step, index) => {
          const progress = step.stepId
            ? progressById.get(step.stepId)
            : undefined;
          const status = progressStepStatus(progress?.status);
          const relatedAttempts = lessonAttempts.filter(
            (attempt) =>
              attempt.journeyStepId === step.stepId ||
              attempt._id === progress?.attemptId ||
              progress?.subStepProgress?.some(
                (sub) => sub.attemptId === attempt._id,
              ),
          );
          const conversationSessions = buildStepConversationSessions({
            stepId: step.stepId,
            progress,
            transcripts,
            liveMessages: messages,
          });
          const messageCount = conversationSessions.reduce(
            (sum, session) => sum + session.messages.length,
            0,
          );

          return (
            <Steps.Step
              key={step.stepId ?? `step-${index}`}
              status={status}
              title={
                <Space size={8} wrap>
                  <span>{stepTitle(step, index)}</span>
                  {statusTag(progress?.status ?? "pending")}
                  <Typography.Text type="secondary">
                    {stepTypeLabel(step.type)}
                  </Typography.Text>
                </Space>
              }
              description={
                <div>
                  {progress?.startedAt && (
                    <div style={{ fontSize: 11, color: "rgba(0,0,0,0.45)" }}>
                      Started: {formatDate(progress.startedAt)}
                    </div>
                  )}
                  {progress?.completedAt && (
                    <div style={{ fontSize: 11, color: "rgba(0,0,0,0.45)" }}>
                      Done: {formatDate(progress.completedAt)}
                    </div>
                  )}
                  <Collapse
                    ghost
                    expandIcon={({ isActive }) =>
                      isActive ? <MinusOutlined /> : <PlusOutlined />
                    }
                    style={{ marginTop: 4, background: "transparent" }}
                  >
                    <Collapse.Panel
                      header={
                        <Typography.Text type="secondary">
                          Details (
                          {relatedAttempts.length} attempts,{" "}
                          {conversationSessions.length} sessions /{" "}
                          {messageCount} messages,{" "}
                          {progress?.subStepProgress?.length ??
                            step.subSteps?.length ??
                            0}{" "}
                          sub-steps)
                        </Typography.Text>
                      }
                      key={`${step.stepId}-details`}
                    >
                      <StepDetailsTree
                        step={step}
                        progress={progress}
                        attempts={relatedAttempts}
                        conversationSessions={conversationSessions}
                        definitionSubSteps={step.subSteps}
                      />
                    </Collapse.Panel>
                  </Collapse>
                </div>
              }
            />
          );
        })}
      </Steps>
    </div>
  );
};

const JourneyLogsModal = ({
  journeyId,
  open,
  onClose,
}: {
  journeyId: string;
  open: boolean;
  onClose: () => void;
}) => {
  const [logs, setLogs] = useState<JourneyLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [progressSortOrder, setProgressSortOrder] = useState<
    "asc" | "desc"
  >();
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [selectedLogId, setSelectedLogId] = useState<string>();
  const [details, setDetails] = useState<JourneyLogDetails>();
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [runs, setRuns] = useState<JourneyRun[]>([]);

  const loadLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const response = await Api.get(
        `/api/admin/panel/journeys/${journeyId}/logs`,
        {
          params: {
            page,
            limit: 25,
            ...(progressSortOrder && {
              sortBy: "progress",
              sortOrder: progressSortOrder,
            }),
          },
        },
      );
      const result = response.data.data;
      setLogs(result?.data ?? []);
      setTotal(result?.total ?? 0);
    } catch {
      message.error("Failed to load journey logs");
    } finally {
      setLoadingLogs(false);
    }
  }, [journeyId, page, progressSortOrder]);

  useEffect(() => {
    if (open) loadLogs();
  }, [open, loadLogs]);

  const openLogDetails = useCallback(
    async (userJourneyId: string) => {
      setSelectedLogId(userJourneyId);
      setDetails(undefined);
      setLoadingDetails(true);
      try {
        const response = await Api.get(
          `/api/admin/panel/journeys/${journeyId}/logs/${userJourneyId}`,
        );
        setDetails(response.data.data);
        setRuns(response.data.data?.runs ?? []);
      } catch {
        message.error("Failed to load user journey details");
        setSelectedLogId(undefined);
      } finally {
        setLoadingDetails(false);
      }
    },
    [journeyId],
  );

  const columns = useMemo(
    (): ColumnsType<JourneyLog> => [
      {
        title: "User",
        key: "user",
        width: 220,
        ellipsis: true,
        render: (_value, record) => (
          <div style={{ lineHeight: 1.3 }}>
            <div>{record.user?.name ?? "Deleted user"}</div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }} ellipsis>
              {record.user?.email ?? record.userID}
            </Typography.Text>
          </div>
        ),
      },
      {
        title: "Started",
        dataIndex: "startedAt",
        render: formatDate,
        width: 160,
      },
      {
        title: "Language",
        key: "language",
        render: (_value, record) =>
          runLanguageLabel(record) ?? languageLabel(record.user),
        width: 130,
        ellipsis: true,
      },
      {
        title: "Status",
        dataIndex: "status",
        render: statusTag,
        width: 110,
      },
      {
        title: "Progress",
        key: "progress",
        sorter: true,
        sortOrder:
          progressSortOrder === "asc"
            ? "ascend"
            : progressSortOrder === "desc"
              ? "descend"
              : undefined,
        render: (_value, record) =>
          `${Math.min(
            record.currentStepIndex + 1,
            record.stepProgress.length,
          )} / ${record.stepProgress.length}`,
        width: 80,
      },
      {
        title: "Actions",
        key: "actions",
        width: 80,
        render: (_value, record) => (
          <Button
            type="link"
            size="small"
            onClick={() => openLogDetails(record._id)}
          >
            Details
          </Button>
        ),
      },
    ],
    [openLogDetails, progressSortOrder],
  );

  const backToLogs = () => {
    setSelectedLogId(undefined);
    setDetails(undefined);
    setRuns([]);
  };

  const close = () => {
    backToLogs();
    onClose();
  };

  const detailContent = details && (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button onClick={backToLogs}>Back to logs</Button>
        {statusTag(details.userJourney.status)}
      </Space>
      <Descriptions bordered size="small" column={{ xs: 1, md: 2, lg: 4 }}>
        <Descriptions.Item label="User">
          <EntityLink
            to={userEditPath(details.user?._id ?? details.userJourney.userID)}
          >
            {details.user?.name ?? "Deleted user"}
          </EntityLink>
        </Descriptions.Item>
        <Descriptions.Item label="User ID">
          <EntityLink
            to={userEditPath(details.user?._id ?? details.userJourney.userID)}
          >
            {details.user?._id ?? details.userJourney.userID}
          </EntityLink>
        </Descriptions.Item>
        <Descriptions.Item label="Email">
          {details.user?.email ?? "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Language">
          {runLanguageLabel(details.userJourney) ?? languageLabel(details.user)}
        </Descriptions.Item>
        <Descriptions.Item label="Level">
          {details.user?.languageLevel ?? "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Started">
          {formatDate(details.userJourney.startedAt)}
        </Descriptions.Item>
        <Descriptions.Item label="Completed">
          {formatDate(details.userJourney.completedAt)}
        </Descriptions.Item>
        <Descriptions.Item label="User journey ID" span={2}>
          {details.userJourney._id}
        </Descriptions.Item>
        <Descriptions.Item label="User XP">
          {details.user?.xp ?? 0}
        </Descriptions.Item>
        <Descriptions.Item label="Progression level">
          {details.user?.progressionLevel ?? "—"}
        </Descriptions.Item>
      </Descriptions>

      <Typography.Title level={5} style={{ marginTop: 20 }}>
        Journey progress
      </Typography.Title>
      <JourneyProgressSteps
        journeySteps={details.journey.steps}
        stepProgress={details.userJourney.stepProgress}
        lessonAttempts={details.lessonAttempts}
        messages={details.messages}
        transcripts={details.userJourney.chatTranscripts}
      />
    </>
  );

  return (
    <Modal
      title={selectedLogId ? "User journey details" : "Journey logs"}
      open={open}
      onCancel={close}
      footer={null}
      width="90vw"
      style={{ top: "5vh", paddingBottom: 0 }}
      bodyStyle={{
        height: "calc(90vh - 110px)",
        overflow: "auto",
        paddingTop: 12,
      }}
      destroyOnClose
    >
      {selectedLogId ? (
        <>
          {runs.length > 1 && (
            <Tabs
              activeKey={selectedLogId}
              onChange={openLogDetails}
              items={runs.map((run, index) => ({
                key: run._id,
                label: (
                  <Space size={6}>
                    <span>
                      {runLanguageLabel(run) ?? `Run ${index + 1}`}
                    </span>
                    {statusTag(run.status)}
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {formatDate(run.startedAt)}
                    </Typography.Text>
                  </Space>
                ),
              }))}
            />
          )}
          {loadingDetails ? <Spin size="large" /> : detailContent}
        </>
      ) : (
        <Table
          loading={loadingLogs}
          columns={columns}
          dataSource={logs}
          rowKey="_id"
          size="small"
          pagination={{
            current: page,
            pageSize: 25,
            total,
            showSizeChanger: false,
            size: "small",
          }}
          onChange={(pagination, _filters, sorter, extra) => {
            if (extra.action === "paginate") {
              setPage(pagination.current ?? 1);
              return;
            }
            if (extra.action !== "sort") return;

            const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
            setProgressSortOrder(
              activeSorter.order === "ascend"
                ? "asc"
                : activeSorter.order === "descend"
                  ? "desc"
                  : undefined,
            );
            setPage(1);
          }}
        />
      )}
    </Modal>
  );
};

export default JourneyLogsModal;
